import { formatDecimalString } from '@/utils/decimal-display';

/**
 * The statement names each amount's direction with one letter, from Exante's
 * side - R = receivable by Exante, P = payable by Exante.
 *
 * The figure keeps the ledger's sign - positive = due to Exante - on the
 * document exactly as in the statements list and Mark paid (Tim, 2026-09-16:
 * "whatever the logic is within LendRail, lets keep it consistent"), and the
 * letter reads from that same side (feedback 2026-10-22, LR-839). Twin of
 * `_pr_mark` in backend/src/apps/billing/services/statement_export.py.
 *
 * String work only, never `Number` (HON-03): these are backend Decimal strings.
 */

/** ISO date the ledger's amounts started carrying a payment direction.
 * Mirrors DIRECTION_LANDED in backend/src/apps/billing/services/statement_builder.py
 * - the accepted duplication the sign legend already carries. Before it a row
 * may hold a bare rate-signed magnitude, so there is no direction to name. */
export const DIRECTION_LANDED = '2026-05-23';

/** Whether a document's period lies wholly in the directional era - the twin of
 * the export's `_is_directional`. A period that STARTS before it - fully-pre or
 * straddling - has rows of mixed provenance, so none of the three things this
 * gates applies: the P/R letters, the Payable / Receivable summary, the legend. */
export const isDirectional = (periodStart: string | null): boolean =>
  periodStart != null && periodStart >= DIRECTION_LANDED;

const ZERO = /^-?0*(?:\.0*)?$/;

export type PrMark = 'P' | 'R' | '';

/**
 * The P/R cell for an amount held in OUR sign: what EXANTE does with it. Blank
 * where there is nothing to say - no figure, or one that rounds away at the
 * cent the document prints.
 *
 * NOTE: rounds half-UP (`formatDecimalString`) where the file's `_pr_mark`
 * rounds half-EVEN, so the two can disagree on an exact half cent (0.005: 'R'
 * here, blank in the file). Unreachable on today's payloads - every figure this
 * is called with arrives at 2dp (`amount`, the summary nets) - and only the
 * fallbacks for an older payload carry 8dp: `net_amount` (pre-LR-619) and a
 * group's or security's `subtotal` (pre-LR-632, `totalPhrase`). If one ever
 * becomes a real path, round half-even here too.
 */
export const prMark = (value: string | null): PrMark => {
  if (value == null) return '';
  const printed = formatDecimalString(value, { dp: 2 }).replace(/,/g, '');
  if (!/\d/.test(printed) || ZERO.test(printed)) return '';
  return printed.startsWith('-') ? 'P' : 'R';
};

/** The block total's sentence - twin of `_total_phrase` in the export. */
export const totalPhrase = (value: string | null): string => {
  const mark = prMark(value);
  if (mark === 'R') return 'Total Receivable by EXANTE';
  if (mark === 'P') return 'Total Payable by EXANTE';
  return '';
};
